// Copyright: IQGeo Limited 2010-2019
import $ from 'jquery';
import _ from 'underscore';
import L from 'leaflet';
import Promise from 'bluebird';
import myw from 'myWorld-base';
import featureEditorHtml from 'text!html/featureEditor.html';
import popupFeatureEditorHtml from 'text!html/featureEditorPopup.html';
import 'jquery-ui';
import 'leaflet';
import 'myWorld/controls/displayMessage';
import 'myWorld/controls/geomDrawMode';
import 'myWorld/controls/geomRotateMode';
import './fieldEditorsPanel';

/**
 * Options for FeatureEditor instances
 * @typedef featureEditorOptions
 * @property {DDFeature}    feature                     The feature to be edited
 * @property {MapControl}   map                         Map on which to edit the feature's geometry
 * @property {domElement}       [el]                        (from Backbone) Element where the editor should render the UI. If not provided, a popup editor will be used
 * @property {boolean}  [useTabs=false]                     Whether self is embeded in a list of multiple of features or not
 * @property {boolean}  [useExpandedFieldEditors=false]     Whether a null value should still be rendered or not
 * @property {boolean}  [useSoftKeyboardInput=true]         Whether to use the softKeyboardInput on touch devices
 */

myw.FeatureEditor = myw.Control.extend({
    options: {
        useTabs: false,
        useExpandedFieldEditors: false,
        useSoftKeyboardInput: true,
        //If true show a pin icon in create object editors
        //Useful for data entry when you have to create multiple objects of the same type
        makeCreateFormPinnable: false,
        fieldEditorsPanelClass: myw.FieldEditorsPanel
    },

    //Icon used when editing a point geometry
    editMarkerIcon: new L.DivIcon({
        iconSize: new L.Point(8, 8),
        iconAnchor: new L.Point(8, 8),
        className: 'leaflet-div-icon edit-marker-selected'
    }),

    template: _.template(featureEditorHtml),
    templatePopup: _.template(popupFeatureEditorHtml),

    events: {
        'click .cancel:not(:disabled)': 'cancel',
        'click .delete:not(:disabled)': 'deleteFeature',
        'click .save:not(:disabled)': 'save',
        'click .rotate:not(:disabled)': 'toggleGeomRotateMode',
        'click .set-map-object': 'geomMode',
        'click .lock-editor': 'toggleLockedState'
    },

    /**
     * @class  A UI control for editing or creating a feature <br/>
     *         Does field validation and activation of Geometry edit mode on the corresponding map.<br/>
     *         Triggers a 'cancelled' event when editing has been cancelled.<br/>
     *         Custom editors can be used by registering sub-classes as feature models. See {@link DDFeature}. <br/>
     *         Field editors are rendered using {@link FieldEditorsPanel}
     * @param  {Application|Control}    owner
     * @param  {featureEditorOptions}           options
     * @constructs
     * @extends {Control}
     * @fires cancelled
     */
    initialize(owner, options) {
        myw.Control.prototype.initialize.call(this, owner, options);

        const feature = options.feature;

        this.popup = !this.options.el; // no element provided means we should popup a dialog for the editor

        this.datasource = feature.datasource;
        this.map = options.map;

        this.feature = feature;
        this.featureDD = feature.featureDD;
        this.referencedFeatureUrn = null;
        this.isLocked = this.owner.isEditorLocked;

        _.bindAll(this, 'onKeydown', 'onKeyup');

        //ensure we have the necessary properties before building the UI
        //ENH: there may be no need to request lobs once image fields have a better editor
        /* Promise fullfilled when the UI is ready
         * @type {Promise} */
        this.ready = this.feature
            .ensure(['simple', 'display_values', 'lobs'])
            .then(() => this.initUI())
            .then(() => {
                this.trigger('ready');
            });
    },

    /**
     * Renders the editor and activates the geometry editing mode
     * @return {Promise} Promise that becomes fullfilled when finished
     */
    initUI() {
        this.render();

        this.activateGeomDrawMode(this.feature, this.feature.getDDGeometryType());
        document.addEventListener('keydown', this.onKeydown);
        document.addEventListener('keyup', this.onKeyup);
        return Promise.resolve();
    },

    /**
     * Activates GeomRotateMode when control key is pressed
     * @param {KeyboardEvent} event Captured keyboard event
     */
    onKeydown(event) {
        if (this.isGeomRotateMode()) return;
        if (this.isRotationKeyCode(event.keyCode)) {
            this.activateGeomRotateMode();
        }
    },

    /**
     * Ends GeomRotateMode when control key is released
     * @param {KeyboardEvent} event Captured keyboard event
     */
    onKeyup(event) {
        if (!this.isGeomRotateMode()) return;
        if (this.isRotationKeyCode(event.keyCode)) {
            this.endGeomRotateMode();
        }
    },

    /**
     * Builds a form for feature editing. <br/>
     * Includes the field editors, and  buttons for geometry mode, saving, canceling and deleting
     * @param  {DDFeature}  feature     The feature to be edited
     * @param  {jQueryElement}  container   The container where the editor should be rendered
     * @private
     */
    render() {
        const feature = this.feature,
            templateValues = this.getTemplateValues(feature);

        if (!this.options.el) {
            //weren't given an element to render on.

            //Updates the jquery ui title to use an html instead of just text
            $.widget(
                'ui.dialog',
                $.extend({}, $.ui.dialog.prototype, {
                    _title(title) {
                        if (!this.options.title) {
                            title.html('&#160;');
                        } else {
                            title.html(this.options.title);
                        }
                    }
                })
            );

            //open a dialog to render on
            this.openPopupDialog();
        }

        const template = this.popup ? this.templatePopup : this.template;
        this.$el.html(template(templateValues));
        if (feature.isNew && this.options.makeCreateFormPinnable) {
            this.$el.children('.panel-header').prepend(this._createPinBtn());
        }
        this.$('.button').button(); //styles buttons with jquery-ui

        this.fieldEditorsEl = this.$('#field-editors');
        this._mapObjectValidationLabel = this.$('.mapObjectValidationLabel');

        this.renderFieldEditors();

        this.translate(this.$el);
        if (this.options.useSoftKeyboardInput) myw.softKeyboardInput.enableOn(this.$el, this.app);
    },

    /**
     * Converts self's element into a dialog and add it to the dom
     */
    openPopupDialog() {
        const isMasterView = this.feature.datasource.options.masterMode;
        const masterIcon = $('<span>', {
            class: 'master-layer-icon',
            title: myw.msg('LayerControl', 'master_layer')
        });

        const titleText = this.options.feature.getTitle(),
            title = isMasterView ? titleText + masterIcon[0].outerHTML : titleText;

        let titleDiv = title;
        if (this.feature.isNew && this.options.makeCreateFormPinnable) {
            // We want to show the lock toggle icon
            const lockToggleIcon = this._createPinBtn();
            titleDiv =
                '<div class="popup-editor-title">' + lockToggleIcon[0].outerHTML + title + '<div>';
        }

        $(':root').append(this.el);
        this.$el.dialog({
            modal: false,
            width: 'auto',
            resizable: false,
            position: { my: 'left top', at: 'left+50 top+50', of: window },
            title: titleDiv,
            closeText: this.msg('close_tooltip'),
            close: (event, ui) => {
                this.cancel();
            }
        });

        this.$el
            .dialog('widget')
            .find('.lock-editor')
            .on('click', ev => this.toggleLockedState(ev));

        // Resize the editor dialog on window resize
        $(window)
            .resize(() => {
                const panelHeight = $(window).height() - 120;
                this.$el.css({
                    'max-height': panelHeight,
                    'overflow-y': 'auto'
                });
            })
            .resize();

        this.delegateEvents();

        //Hide the left panel and the feature brief (that shows up as a result of closing the pane)
        this.app.layout.layout.panes.west.hide(); //to avoid the animation masking the dialog
        this.app.layout.close('west');
        this.app.layout.controls.featureBriefControl.hide();
    },

    /**
     * Renders field editors.
     * If options.useTabs is true and field groups definitions exist <br/>
     * it will render using several tabs - one per group. Each tab is rendered using {@link FieldEditorsPanel} <br/>
     * Otherwise it renders all fields using a single {@link FieldEditorsPanel}
     */
    renderFieldEditors() {
        const groups = this.feature.getFieldGroups();
        this.tabbed = this.options.useTabs && (groups && groups.length > 1);

        if (this.tabbed) {
            //there are more than 1 field group definitions. Create a tab for each group
            this.renderTabs();
        } else {
            //only one panel with fieldEditors
            this.renderFieldEditorsSinglePane();
        }

        _.each(this.fieldPanels, fieldPanel => {
            //propagate value change events
            this.listenTo(fieldPanel, 'change', this._propagateEvent);
        });
    },

    /**
     * Renders all field editors as a single panel
     */
    renderFieldEditorsSinglePane() {
        // jshint newcap: false
        const fieldEditorsPanelClass = this.options.fieldEditorsPanelClass;

        const feature = this.feature;
        const fieldNames = feature.getFieldsOrder();
        let fieldEditorsPanel;

        if (this.feature.isNew) this.addKeyFieldTo(fieldNames);

        fieldEditorsPanel = new fieldEditorsPanelClass(this, {
            el: this.fieldEditorsEl,
            feature: feature,
            fields: fieldNames,
            useExpandedFieldEditors: this.options.useExpandedFieldEditors
        });

        this.fieldPanels = [fieldEditorsPanel];
    },

    /**
     * Renders field editors grouping them in tabs acording to field group definitions
     */
    renderTabs() {
        //get tab definitions
        const tabs = this._getTabDefinitions();

        this.tabControl = new myw.TabControl(this, {
            el: this.$('#field-editors'),
            tabs: tabs,
            initialTab: tabs[0].id
        });

        // Add a top border to the tab buttons
        this.tabControl._tabButtons.addClass('top-bordered');

        //set the list of the field panels for simpler access when obtaining changes and validating (same as non-tabbed editor)
        this.fieldPanels = [];
        //Normally would have to wait for tabControl to be ready but since we are
        //not using the tabControl's html option, our tabs are rendered synchronously,
        //so we don't need to wait for initialized to fulfill
        _.each(_.values(this.tabControl.tabs), tab => {
            if (tab.control) {
                //store the tab button with the control to make it easier to access when we need to highlight the button
                tab.control.button = tab.button;
                this.fieldPanels.push(tab.control);
                this.listenTo(tab.control, 'change', this._propagateEvent);
            }
        });
    },

    /**
     * Returns the list of tab definitions to render in the UI
     * @return {Array<tabDefinition>}
     * @private
     */
    _getTabDefinitions() {
        const tabs = [];
        _.each(this.feature.getFieldGroups(), fieldGroup => {
            const fields = _.pluck(fieldGroup.fields, 'field_name');
            tabs.push({
                id: this.getSafeIdFrom(fieldGroup.display_name),
                title: fieldGroup.display_name,
                control: [
                    this.options.fieldEditorsPanelClass,
                    {
                        feature: this.feature,
                        fields: fields,
                        useExpandedFieldEditors: this.options.useExpandedFieldEditors
                    }
                ]
            });
        });

        if (this.feature.isNew) this.addKeyFieldTo(this.feature, tabs[0].control[1].fields);
        return tabs;
    },

    /**
     * If the key field does not have a generator and is not included in the fieldNames,
     * adds it to the fieldNames
     * @param {array<string>} fieldNames   List of field names to show in the editor
     */
    addKeyFieldTo(fieldNames) {
        const featureDD = this.feature.featureDD;
        const keyField = featureDD.key_name;
        const needsKeyField =
            !_.contains(fieldNames, keyField) && !featureDD.fields[keyField].generator;
        if (needsKeyField) {
            fieldNames.unshift(keyField); //Pushes the keyfield to the top of the array
        }
    },

    /**
     * Obtains the values to use in the html template.
     * Override in sub-classes if using a diferent template
     * @param  {DDFeature} feature
     * @return {object}         keyed on field name
     */
    getTemplateValues(feature) {
        const featureDD = this.featureDD;
        const templateValues = {};
        let geom_not_editable = false;

        //Set the map object type
        let geomTypeStr = featureDD.geometry_type;
        if (!feature.isNew && feature.hasGeometry() && this._hasMutiGeometry(feature)) {
            // Feature has a geometry of a "Multi" type - no support for editing it
            geomTypeStr = this.msg('not_editable', { geomtype: feature.getGeometryType() });
            geom_not_editable = true;
        }
        templateValues.geom_not_editable = geom_not_editable;
        templateValues.geomType = geomTypeStr;
        templateValues.deletable = !feature.isNew && featureDD.delete_from_gui;

        //figure out title
        let title = feature.getTitle();
        if (feature.isNew) title = this.msg('detached_feature_title', { title: title });
        templateValues.myw_title = title;

        templateValues.geomName =
            featureDD.geometry_type && feature.primaryGeomFieldDD().external_name;

        if (feature.isNew) {
            templateValues.set_map_object_geometry = this.msg('set_map_object_geometry', {
                geomName: templateValues.geomName
            });
        } else {
            templateValues.set_map_object_geometry = this.msg('update_map_object_geometry', {
                geomName: templateValues.geomName
            });
        }
        templateValues.myw_short_description = feature.getShortDescription();
        templateValues.masterView = feature.datasource.options.masterMode;
        templateValues.isLocked = this.isLocked;
        return templateValues;
    },

    /**
     * Returns true is the feature is a multipoint, multiline or multipoygon
     * @param  {DDFeature} feature
     * @return {Boolean}
     * @private
     */
    _hasMutiGeometry(feature) {
        return (
            this.featureDD.geometry_type.toLowerCase() !== feature.getGeometryType().toLowerCase()
        );
    },

    _propagateEvent(ev) {
        this.trigger('change', ev);
    },

    /* ******************** Geom editing methods ********************* */

    /**
     * Returns true if the current map interaction mode is a GeomRotateMode
     */
    isGeomRotateMode() {
        return this.map.currentInteractionMode() instanceof myw.GeomRotateMode;
    },

    /**
     * Returns true if the current map interaction mode is a GeomDrawMode
     */
    isGeomDrawMode() {
        return this.map.currentInteractionMode() instanceof myw.GeomDrawMode;
    },

    /**
     * Activates geometry edit mode for a given feature
     * @param  {DDFeature}  feature     Feature being edited/created
     * @param  {string}     geomType    Type of geometry to edit/create
     */
    activateGeomDrawMode(feature, geomType) {
        let editOverlay, geomDrawMode;

        if (!this.featureDD.geometry_type) {
            return;
        } else if (!feature.hasGeometry()) {
            editOverlay = null;
        } else if (geomType.toLowerCase() == feature.getGeometryType().toLowerCase()) {
            // edit existing geometry
            editOverlay = this._getEditOverlay(this.map, feature, geomType);
        } else {
            // Feature has a geometry of a "Multi" type - no support for editing it
            return;
        }

        const geomFieldName = feature.featureDD.primary_geom_name;
        const orientationFieldName = `myw_orientation_${geomFieldName}`;
        const rotatable = feature.featureDD.fields.hasOwnProperty(orientationFieldName);
        geomDrawMode = this.geomDrawMode = new myw.GeomDrawMode(this.map);
        geomDrawMode.setGeomType(geomType);
        geomDrawMode.setEditOverlay(
            editOverlay,
            rotatable,
            feature.properties[orientationFieldName]
        );
        this.map.setInteractionMode(geomDrawMode);
    },

    /**
     * Obtains a Leaflet overlay suitable to be edited by the user using  geomDrawMode
     * @param  {MapControl} map         Map on which to edit/create the geometry
     * @param  {Feature}    feature     Feature representation of the feature being edited, if there is one
     * @param  {string}         geomType    Type of the geometry to be edited/created
     * @return {ILayer}            Marker, polyline or polygon
     * @private
     */
    _getEditOverlay(map, feature, geomType) {
        const featureRep = map.getFeatureRepFor(feature);

        this._pointFeatureRep = null;

        if (featureRep) {
            if (geomType == 'Point') {
                //the overlay is a red circle which is not good to specify a new location,
                //so we use a smaller square that makes it easier to locate correctly
                this._editOverlay = new L.Marker(featureRep.getCenter());
                this._editOverlay.setIcon(this.editMarkerIcon);
                //save the circle highlight so we can put it back if necessary
                this._pointFeatureRep = featureRep;
                map.removeFeatureReps([feature]);

                map.addLayer(this._editOverlay);
            } else {
                this._editOverlay = featureRep.getEditOverlay();
            }
        } else {
            this._editOverlay = null;
        }
        return this._editOverlay;
    },

    /**
     * Disables the geom editing mode, going back to previous mode
     */
    endGeomDrawMode() {
        const map = this.map;

        if (this.isGeomRotateMode()) this.endGeomRotateMode();

        if (this.geomDrawMode) {
            if (this.feature.isNew && this._editOverlay) {
                //edit overlay was created - it wasn't an existing overlay such as from the current feature
                //so we need to remove it from the map
                map.removeLayer(this._editOverlay);
            } else if (this._pointFeatureRep) {
                //remove from the map the layer that was added for the user to move a point feature
                map.removeLayer(this._editOverlay);
                //restore the feature highlight
                if (this._pointFeatureRep.feature == this.app.currentFeature) {
                    map.highlightFeature(this.app.currentFeature);
                }
            }
            //reset temporary properties
            this._pointFeatureRep = null;
            this._editOverlay = null;

            this.map.endCurrentInteractionMode();
            this.geomDrawMode = null;
        } else {
            // geometry wasn't editable so we weren't really in geom draw mode
        }
    },

    /**
     * Activates geometry rotating mode when we are editing a geometry
     */
    activateGeomRotateMode() {
        if (this.isGeomRotateMode()) return;
        if (!this.geomDrawMode) return;
        this.geomDrawMode.activateGeomRotateMode();
    },

    /**
     * Disables the GeomRotateMode, going back to previous mode
     */

    endGeomRotateMode() {
        if (!this.isGeomRotateMode()) return;
        if (!this.geomDrawMode) return;
        this.geomDrawMode.endGeomRotateMode();
    },

    /**
     * Activate GeomRotateMode, or ending GeomRotateMode if already active
     */

    toggleGeomRotateMode() {
        if (!this.isGeomRotateMode()) {
            this.activateGeomRotateMode();
        } else {
            this.endGeomRotateMode();
        }
    },

    /* **************************  Field value manipulation  ********************* */
    /**
     * Obtain values, including the changes made by the user, to send to the database
     * Values are obtained from the html input elements
     * @param  {DDFeature}  feature     Feature being inserted/updated
     * @return {geojson}                    Feature data in geojson format
     */
    getChanges(feature) {
        if (this.isGeomRotateMode()) this.endGeomRotateMode();

        const drawnGeometry = this.geomDrawMode && this.geomDrawMode.getGeometry();
        const orientation = this.geomDrawMode && this.geomDrawMode.getRotation();
        const coords = drawnGeometry && drawnGeometry.coordinates && drawnGeometry.coordinates[0];
        let editorValues;

        //close polygons automatically
        if (
            drawnGeometry &&
            drawnGeometry.type == 'Polygon' &&
            coords.length > 2 &&
            !_.isEqual(coords[0], coords[coords.length - 1])
        ) {
            coords.push(coords[0]);
        }

        const featureData = {
            type: 'Feature',
            properties: _.extend({}, feature.properties),
            geometry: drawnGeometry || feature.getGeometry() || {}
        };

        if (orientation != null) {
            const geomFieldName = feature.featureDD.primary_geom_name;
            const orientationFieldName = `myw_orientation_${geomFieldName}`;
            featureData.properties[orientationFieldName] = orientation;
        }

        //get changes from each of the forms and merge them
        _.each(this.fieldPanels, fieldPanel => {
            editorValues = fieldPanel.getValues(feature);
            _.extend(featureData.properties, editorValues);
        });
        //exclude calculated fields from data to send
        _.each(feature.featureDD.fields, fieldDD => {
            if (fieldDD.value) delete featureData.properties[fieldDD.internal_name];
        });
        return featureData;
    },

    /**
     * Returns the field editor being used to edit a given field
     * @param  {string} fieldName Name of the field
     * @return {FieldEditor}
     */
    getFieldEditor(fieldName) {
        const fieldPanel = _.find(
            this.fieldPanels,
            fieldPanel => !!fieldPanel.fieldEditors[fieldName]
        );

        return fieldPanel.fieldEditors[fieldName];
    },

    /**
     * Returns the current editor value for a given field
     * @param  {string} fieldName Name of the desired field
     * @return {string|number}           current value of the field editor
     */
    getValue(fieldName) {
        return this.getFieldEditor(fieldName).getValue();
    },

    /**
     * Sets the current value of a field editor
     * @param {string} fieldName Name of the field
     * @param {string|number|date|object} value     New value for the field
     */
    setValue(fieldName, value) {
        return this.getFieldEditor(fieldName).setValue(value);
    },

    /* ************************ Data Validation ********************* */

    /**
     * Validates the geometry and field values according to the data dictionary
     * If a value is invalid this information is presented to the user
     * @param  {geojson}    featureJson Feature data to be validated
     * @return {boolean}                Whether the provided data is valid or not
     */
    validateChanges(featureJson) {
        let validFormData = true,
            validGeom,
            isValid;

        _.each(this.fieldPanels, fieldPanel => {
            isValid = fieldPanel.validateChanges(featureJson);
            validFormData = validFormData && isValid;

            if (fieldPanel.button) {
                if (!isValid) fieldPanel.button.addClass('invalid-tab-content');
                else fieldPanel.button.removeClass('invalid-tab-content');
            }
        });
        const geomEnabled = !!this.featureDD.geometry_type;
        if (geomEnabled) {
            // validate geometry only if feature has geometry
            validGeom = this._validateGeometry(featureJson.geometry);
            this._mapObjectValidationLabel.html(validGeom.message || '');
        }
        const isDataValid = geomEnabled ? validFormData && validGeom.isValid : validFormData;
        if (!isDataValid) this.displayMessage(this.msg('invalid_data'), 'error');

        return isDataValid;
    },

    /**
     * Elementry geoJson geometry validation
     * @param  {geoJson}  geometry
     * @return {object} an object with two keys: isValid (boolean) and message (string, an error message)
     * @private
     */
    _validateGeometry(geometry) {
        //ENH: more robust geoJson validation, break into smaller methods
        const type = geometry.type,
            coordinates = geometry.coordinates,
            result = { isValid: false, message: null };

        if (!coordinates) {
            result.message = this.msg('place_point');
        } else if (type == 'Polygon') {
            result.message = this._validatePolygonGeom(coordinates);
            if (!result.message) result.isValid = true;
        } else if ((type == 'LineString' || type == 'Linestring') && coordinates.length < 2) {
            result.message = this.msg('complete_linestring');
        } else {
            result.isValid = true;
        }
        return result;
    },

    /**
     * Checks if the polygon is valid
     * @param  {array} coordinates  List of geometry coordinates
     * @return {string}             If the polygon is invalid, returns an appropriate message
     * @private
     */
    _validatePolygonGeom(coordinates) {
        if (coordinates[0].length < 4) {
            //polygons are "closed" so should have at least 4 points
            return this.msg('complete_polygon');
        } else {
            this.geomDrawMode._validatePolygon();
            if (!this.geomDrawMode.isPolygonValid) return this.msg('invalid_polygon');
        }
    },

    /* ************************ Database operations********************* */

    /**
     * Handler for the save button.
     * Gets the changes, validates them and if valid saves them to the database
     */
    async save() {
        const feature = this.feature;
        const featureJson = this.getChanges(feature);
        const validated = this.validateChanges(featureJson);
        let request;
        const isNew = feature.isNew;

        if (validated) {
            if (isNew) {
                request = this.insertFeature(featureJson);
            } else {
                request = this.updateFeature(featureJson);
            }

            //for the situation where the save takes some time,
            //disable the buttons (to avoid the user repeating the save thinking we could cancel the save that is already on its way)
            this.$('.button').attr('disabled', true);
            //and display an information message
            this.displayMessage(this.msg('saving'), 'alert');

            const feature = await Promise.resolve(request)
                .catch(myw.ObjectNotFoundError, error => {
                    //created object is not accessible (due to filters)
                    this.trigger('created_not_accessible', this.msg('created_not_accessible'));
                    this.close();
                })
                .catch(reason => {
                    let rethrow = true,
                        message;

                    if (reason.messageGroup && reason.messageId) {
                        message = myw.msg(reason.messageGroup, reason.messageId);
                        rethrow = false;
                    } else if (reason instanceof myw.DuplicateKeyError) {
                        // Display inline validation error under keyFieldName input
                        const input = this.getInputFromFieldName(this.feature.keyFieldName);
                        this.displayErrorFor(input, 'duplicate_key');
                        // Display invalid data at bottom of editor
                        message = this.msg('invalid_data');
                        rethrow = false;
                    } else {
                        // Unexpected error
                        message = this.msg('problem_saving');
                    }
                    this.displayMessage(message, 'error');
                    this.$('.button').attr('disabled', false); // Activate the buttons again so another action can be performed
                    if (rethrow) {
                        throw reason;
                    }
                });

            if (!feature) return;
            const changeType = isNew ? 'insert' : 'update';
            this.app.fire('featureCollection-modified', {
                changeType: changeType,
                feature: feature,
                featureType: feature.getType()
            });

            await Promise.delay(1000); //wait for a second before closing the editor (so the user can see the success message)

            if (!feature) return;
            this.close();
            this.trigger('saved', { feature, isLocked: this.isLocked });
        }
    },

    /**
     * Display error for specific input field in feature editor
     * Example usage is when rule is not present from config,
     * but on saving produces server error so needs to be validated eg duplicate object
     * @param {object} input field in feature editor
     */
    displayErrorFor(input, message) {
        input.addClass('validationHighlight');
        // Add inline validation message
        input.siblings('.inlineValidation').html(this.msg(message, { key: input[0].value }));
    },

    /**
     * Returns jQuery input element from first fieldPanel with fieldEditor: fieldname
     * @param {string} fieldName name of required field
     */
    getInputFromFieldName(fieldName) {
        if (this.fieldPanels.length == 1) return this.fieldPanels[0].fieldEditors[fieldName].$el;
        for (let i = 0; i < this.fieldPanels.length; i++) {
            const fieldPanel = this.fieldPanels[i];
            if (fieldPanel.fieldEditors[fieldName]) {
                const input = fieldPanel.fieldEditors[fieldName].$el;
                return input;
            }
        }
    },

    /**
     * Handler for the delete button
     */
    deleteFeature() {
        const self = this;

        new myw.Dialog({
            contents: this.msg('confirm_delete_message'),
            destroyOnClose: true,
            title: this.msg('confirm_delete_title'),
            buttons: {
                OK: {
                    text: this.msg('ok_btn'),
                    click() {
                        this.close();
                        self._confirmedDelete();
                    }
                },
                Cancel: {
                    text: this.msg('cancel_btn'),
                    class: 'right',
                    click() {
                        this.close();
                    }
                }
            }
        });
    },

    /**
     * Deletes the feature
     * @private
     */
    async _confirmedDelete() {
        const feature = this.feature;

        try {
            await feature.preDelete(this.app);

            //send delete to database (via transaction that can be defined in feature's model)
            const transaction = await feature.buildDeleteTransaction();
            await this.datasource.runTransaction(transaction);
        } catch (reason) {
            this.displayMessage(this.msg('deleted_not', { type: feature.type }), 'error');
            throw reason;
        }
        await feature.posDelete(this.app);

        this.displayMessage(this.msg('deleted_ok', { title: feature.getTitle() }));
        // fire an event so that feature is removed from the layer
        this.app.fire('featureCollection-modified', {
            changeType: 'delete',
            feature: feature,
            featureType: feature.getType()
        });
        await Promise.delay(1000);

        this.close();
        // fire an event so that feature is removed from the navigation stack
        this.app.fire('currentFeature-deleted');
        this.app.setCurrentFeatureSet([]);
    },

    /**
     * Inserts a new feature into the database
     * @param  {featureData} featureJson
     * @return {promise<DDFeature>}
     */
    async insertFeature(featureJson) {
        let feature = this.feature;

        //start by running any preInsert hook
        await feature.preInsert(featureJson, this.app);

        //obtain from the feature model a transaction to perform the insertion
        const { transaction, opIndex } = await feature.buildInsertTransaction(featureJson);
        const res = await this.datasource.runTransaction(transaction);

        //get feature from database (gets values updated by database triggers)
        const id = res.ids[opIndex];
        feature = await this.datasource.getFeature(feature.getType(), id);

        //run post insert hook
        await feature.posInsert(featureJson, this.app);
        this.displayMessage(this.msg('created_ok', { title: feature.getTitle() }));
        return feature;
    },

    /**
     * Sends a set of changes to a feature to the database
     * @param  {featureData} featureJson
     * @return {promise<DDFeature>}
     */
    async updateFeature(featureJson) {
        let feature = this.feature;
        const preUpdateGeoJson = feature.asGeoJson();
        //start by running pre update hook
        await feature.preUpdate(featureJson, this.app);

        //send changes to database (via transaction that can be defined in feature's model)
        const transaction = await feature.buildUpdateTransaction(featureJson);
        await this.datasource.runTransaction(transaction);

        await feature.update(); //refresh feature properties
        await feature.posUpdate(preUpdateGeoJson, this.app); //run post update hook
        this.displayMessage(this.msg('saved_ok'));
        return feature;
    },

    /**
     * Activates the Editor's digitizing mode by hiding the fields,
     * so that the user can use the map to set a geometry
     */
    geomMode() {
        if (this.popup) {
            this.$el.dialog('widget').toggle();

            this.openSetMapObjectDialog();
        }
    },

    /**
     * Opens a (small) dialog that instructs the user to define a geometry on the map
     */
    openSetMapObjectDialog() {
        const setMapObjectDialog = $(`<div>${this.getGeomModeMsg()}</div>`).dialog({
            modal: false,
            width: 'auto',
            resizable: false,
            position: { my: 'left top', at: 'left+50 top+50', of: window },
            title: this.options.feature.getTitle(),
            buttons: {
                Done: {
                    text: this.msg('done'),
                    class: 'primary-btn',
                    click() {
                        $(this).dialog('close');
                    }
                }
            },
            close: (event, ui) => {
                this.$el.dialog('widget').toggle();
            }
        });
        setMapObjectDialog
            .dialog('widget')
            .find('.ui-dialog-buttonset')
            .css({ float: 'none', 'text-align': 'center' });
    },

    getGeomModeMsg() {
        const feature = this.options.feature;
        const geomType = feature.geometry ? feature.geometry.type : this.featureDD.geometry_type;
        const geomName = feature.primaryGeomFieldDD().external_name;
        let messageType = '';
        let message = '';

        if (feature.isNew) {
            switch (geomType) {
                case 'linestring':
                    messageType = 'new_line_msg';
                    break;
                case 'polygon':
                    messageType = 'new_polygon_msg';
                    break;
                default:
                    // Point
                    messageType = 'new_point_msg';
                    break;
            }
            message = this.msg(messageType, { geomName: geomName });
        } else {
            message = this.msg('edit_geom_msg');
        }
        return message;
    },

    displayMessage(message, type) {
        new myw.DisplayMessage({ el: this.$('.message-container'), type: type, message: message });

        // Scroll to the bottom of the container element so the message is clearly visible
        const el = this.options.el ? this.options.el[0] : this.el;
        el.scrollTop = el.scrollHeight;
    },

    /**
     * Handler for when the user cancels the editing <br/>
     * Either by clicking the cancel button or the dialog close button.
     * Closes the editor and triggers the 'cancelled' event
     */
    cancel() {
        this.close();

        this.trigger('cancelled');
    },

    /**
     * Removes UI elements, clears event handlers, ends geometry draw mode
     */
    close() {
        this.endGeomDrawMode();
        document.removeEventListener('keydown', this.onKeydown);
        document.removeEventListener('keyup', this.onKeyup);

        if (!this.options.el) {
            //we added our own element to the dom to render the editor
            //remove the element from the dom
            this.remove();
            this.app.layout.open('west');
        }

        _.invoke(this.fieldPanels, 'close');

        //self can be reused, so we need to remove event handlers, otherwise we can get duplicate ones
        this.undelegateEvents();
    },

    /*
     * Creates a pin/unpin toggle button
     * @return {jQueryElement}
     */
    _createPinBtn() {
        const lockToggleIcon = $('<span>', { class: 'lock-editor' });
        this._setPinState(lockToggleIcon);
        return lockToggleIcon;
    },

    /*
     * Assigns the active class to the icon if the editor is pinned and vice versa
     * Assigns an appropriate title to the icon
     * @param  {jQueryElement} pinIcon
     */
    _setPinState(pinIcon) {
        const titleMsg = this.isLocked ? 'unpin_editor' : 'pin_editor';
        pinIcon
            .toggleClass('active', this.isLocked)
            .attr('title', myw.msg('FeatureEditor', titleMsg));
    },

    /*
     * Return true if keyCode is recognised as rotation hotkey
     * @param  {string} keyCode  event.key
     */
    isRotationKeyCode(keyCode) {
        //ctrl key = 17,
        //mac Left command = 91 in chrome and 224 in firefox
        return keyCode == 17 || keyCode == 91 || keyCode == 224;
    },

    /**
     * Toggles the isLocked flag and sets the state of the pin icon
     */
    toggleLockedState(ev) {
        this.isLocked = !this.isLocked;
        this._setPinState($(ev.currentTarget));
    }
});

export default myw.FeatureEditor;
